import React from "react";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { MetricCard, GlassCard, PageTitle, ProgressBar, SkeletonGrid, StatusBadge, LuxuryButton } from "@/components/lux";
import GlobalPools from "@/components/GlobalPools";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Wallet, TrendingUp, Users, Network, Trophy, Banknote, Gem, Zap, ArrowUpRight, Clock, CheckCircle2, CandlestickChart, Coins, ShieldCheck, Globe2, BarChart3,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function Dashboard() {
  const { data, loading, reload } = useFetch("/user/dashboard");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = React.useState(Date.now());
  const [roiPopup, setRoiPopup] = React.useState(null);
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Poll for server-side ROI boost unlock events so the member sees the
  // congratulations popup as soon as a qualifying direct is activated.
  React.useEffect(() => {
    let alive = true;
    const checkBoostNotifications = async () => {
      try {
        const r = await api.get("/user/notifications");
        if (!alive) return;
        const boosts = (r.data?.notifications || []).filter(
          (n) => n.kind === "roi_boost" && n.read === false
        );
        if (boosts.length) {
          const latest = boosts[boosts.length - 1];
          setRoiPopup(latest);
        }
        if (boosts.length) {
          await api.post("/user/notifications/roi-boost/read");
        }
      } catch (_) {
        // Notification polling must never block the dashboard.
      }
    };
    const first = setTimeout(checkBoostNotifications, 300);
    const poll = setInterval(checkBoostNotifications, 5000);
    return () => {
      alive = false;
      clearTimeout(first);
      clearInterval(poll);
    };
  }, []);

  const fmtLeft = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    // Always show seconds so the member can visibly see the 168h timer running.
    return d > 0
      ? d + "d " + h + "h " + m + "m " + String(sec).padStart(2, "0") + "s"
      : String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0") + ":" + String(sec).padStart(2, "0");
  };
  const doRetop = async () => {
    try { await api.post("/user/retop", { amount: data.cycle.package }); toast.success("New cycle started"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (loading || !data) return <><PageTitle title="Dashboard" /><SkeletonGrid /></>;

  const b = data.balances;
  const legData = [
    { name: "Left", v: data.left_business },
    { name: "Right", v: data.right_business },
    { name: "Matched", v: data.matched_business },
  ];

  return (
    <>
    <div className="space-y-6" data-testid="user-dashboard">
      <div className="alexa-hero-banner relative overflow-hidden rounded-3xl" data-testid="dashboard-hero">
        <div className="alexa-hero-grid" />
        <div className="alexa-hero-glow alexa-hero-glow-a" />
        <div className="alexa-hero-glow alexa-hero-glow-b" />
        <div className="alexa-hero-chart" aria-hidden="true">
          <span style={{height:"28%"}} /><span style={{height:"42%"}} /><span style={{height:"34%"}} /><span style={{height:"58%"}} />
          <span style={{height:"50%"}} /><span style={{height:"72%"}} /><span style={{height:"64%"}} /><span style={{height:"88%"}} />
        </div>
        <div className="alexa-hero-coins" aria-hidden="true"><Coins size={110}/></div>
        <div className="alexa-hero-content">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-[#F6D36A]"><span className="h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_14px_#34d399]" /> ALEXA FINTECH</p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-white sm:text-5xl">Welcome <span className="text-[#F6D36A]">back</span></h1>
            <p className="mt-1 text-base font-semibold text-white/85">Trade · Earn · Grow Together</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <div className="alexa-hero-chip"><BarChart3 size={15}/> AI Powered Trading</div>
              <div className="alexa-hero-chip"><ShieldCheck size={15}/> Secure & Transparent</div>
              <div className="alexa-hero-chip"><Coins size={15}/> Daily ROI Program</div>
              <div className="alexa-hero-chip"><Globe2 size={15}/> Global Opportunity</div>
            </div>
          </div>
          <div className="alexa-hero-balance">
            <span>Current Balance</span>
            <strong><Wallet size={20}/> $ {money(b.available)}</strong>
          </div>
        </div>
      </div>

      {/* Trading terminal — front and center on the dashboard */}
      <TradingTeaser status={data.status} />

      {/* Prominent animated ACTIVATE action right below the live chart */}
      {data.status !== "active" ? (
        <div className="relative overflow-hidden rounded-3xl p-6 text-center" data-testid="dashboard-activate-panel"
          style={{ background: "linear-gradient(160deg,#0b3d24,#16a34a)", border: "1px solid rgba(34,197,94,0.5)" }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/80">Start Earning</p>
          <h3 className="mt-1 font-display text-2xl font-extrabold text-white">Activate your ID to begin daily ROI</h3>
          <p className="mx-auto mt-1 max-w-lg text-sm text-white/85">Use your fund balance to activate. Your account is currently pending.</p>
          <button onClick={() => navigate("/app/activate")} data-testid="dashboard-activate-btn"
            className="activate-glow mx-auto mt-5 inline-flex items-center gap-2 rounded-2xl bg-white px-8 py-4 text-lg font-extrabold text-[#0b3d24] transition hover:scale-[1.03]">
            <Zap size={20} className="text-[#16a34a]" /> ACTIVATE NOW
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm font-semibold text-emerald-400" data-testid="dashboard-active-pill">
          <CheckCircle2 size={16} /> Account active — your ALEXA trading terminal is ready above.
        </div>
      )}

      {user && (
        <GlassCard className="p-6" data-testid="grow-network">
          <h3 className="mb-1 font-semibold">Grow Your Network</h3>
          <p className="mb-4 text-sm text-[var(--text-dim)]">Share your referral links to earn commissions.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {[["Left", "left"], ["Right", "right"]].map(([label, side]) => {
              const url = `${window.location.origin}/register?sponsor=${user.member_id}&side=${side}`;
              return (
                <div key={side} className="rounded-xl glass-2 p-3">
                  <p className="mb-1 text-xs font-bold" className={side === "left" ? "text-emerald-300" : "text-amber-300"}>{label} Referral Link</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={url} className="w-full truncate rounded-lg border border-[var(--line)] bg-white/5 px-2 py-1.5 text-xs text-[var(--text)]" data-testid={`ref-${side}`} />
                    <LuxuryButton size="sm" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }} data-testid={`copy-${side}`}>Copy</LuxuryButton>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Earnings" value={`$${money(b.total_income)}`} icon={TrendingUp} accent />
        <MetricCard label="Withdrawable" value={`$${money(b.available)}`} icon={Wallet} tone="royal" sub={`Pending $${money(b.pending)}`} />
        <MetricCard label="Wallet (Deposit)" value={`$${money(b.deposit_balance)}`} icon={Wallet} tone="royal" sub="Usable to activate IDs" />
        <MetricCard label="Self Package" value={`$${money(data.package)}`} icon={Zap} tone="white" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <MetricCard label="Current Rate" value={`${data.current_rate}%`} sub={data.roi_enabled ? `Boost L${data.boost_level} · Daily $${money(data.daily_applicable)}` : "Performance engine disabled by operator"} icon={TrendingUp} accent />
        <MetricCard label="Direct Referrals" value={data.direct_count} sub={`${data.active_direct_count} active · Bonus $${money(data.direct_bonus_total)}`} icon={Users} tone="maroon" />
        <MetricCard label="Activity IB Salary" value={`$${money(data.salary_daily)}`} sub={data.salary_target ? `$${money(data.salary_target, 0)} slab` : "No slab yet"} icon={Banknote} />
      </div>

      {data.roi_enabled && data.boost_progress?.length > 0 && (() => {
        const winHrs = data.boost_progress[data.boost_progress.length - 1].hours;
        const left = data.activated_at ? new Date(data.activated_at).getTime() + winHrs * 3600000 - now : null;
        return (
        <GlassCard className="p-6" data-testid="boost-widget">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-semibold text-white"><Zap size={18} className="text-[var(--gold)]" /> ROI Boost Progress</h3>
            {left != null && (left > 0
              ? <span className="flex items-center gap-1.5 rounded-full bg-[var(--gold)]/10 px-3 py-1 text-xs font-bold text-[#F5C84B]" data-testid="boost-timer"><Clock size={13} /> {fmtLeft(left)} left in your {winHrs}h window</span>
              : <span className="rounded-full glass-2 px-3 py-1 text-xs font-semibold text-[var(--text-dim)]" data-testid="boost-timer">{winHrs}h window closed</span>)}
          </div>
          <p className="mb-4 text-xs text-[var(--text-dim)]">
            One {winHrs}h window starts when you activate. Add active directs whose package is ≥ your ${money(data.package)} to raise your self-ROI: <b>3 → 1.5%</b>, <b>+1 more → 2%</b>, <b>+2 more → 3%</b>. No limit on total directs — only this window limits how long you can increase your ROI. Current rate: <span className="gold-text font-bold">{data.current_rate}%</span>
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {data.boost_progress.map((t, i) => (
              <div key={i} className={`rounded-xl glass-2 p-4 ${t.achieved ? "ring-2 ring-[var(--gold)]" : ""}`} data-testid={`boost-tier-${t.rate}`}>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-extrabold text-[#F5C84B]">{t.rate}%</span>
                  {t.achieved
                    ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">UNLOCKED</span>
                    : <span className="text-xs font-bold text-[var(--maroon)]">{t.have}/{t.required}</span>}
                </div>
                <p className="mt-1 text-xs text-[var(--text-dim)]">{t.required} active directs</p>
                <div className="mt-2"><ProgressBar value={t.have} max={t.required} /></div>
                {!t.achieved && <p className="mt-1.5 text-[11px] text-[var(--text-dim)]">{Math.max(t.required - t.have, 0)} more to unlock {t.rate}%</p>}
              </div>
            ))}
          </div>
        </GlassCard>
        );
      })()}

      {data.cycle && (
        <GlassCard className="p-6" data-testid="cycle-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Earning Cycle #{data.cycle.number} · <span className="gold-text font-bold">{data.cycle.multiplier}X</span></h3>
            <StatusBadge status={data.cycle.status} />
          </div>
          <ProgressBar value={data.cycle.earned} max={data.cycle.cap || 1} />
          <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-[var(--text-dim)]">
            <span>Package ${money(data.cycle.package)} · Cap ${money(data.cycle.cap)}</span>
            <span>Earned ${money(data.cycle.earned)} · Remaining <span className="gold-text font-semibold">${money(data.cycle.remaining)}</span></span>
          </div>
          <p className="mt-2 text-[11px] text-[var(--text-dim)]">Cap counts Self ROI + Referral Bonus + Performance IB only. Complete the cycle, then Retop the same ID from the Deposit page.</p>
          {data.cycle.status === "completed" && (
            <LuxuryButton size="sm" variant="maroon" className="mt-3" onClick={doRetop} data-testid="dash-retop">
              Retop ${money(data.cycle.package)} — Start New Cycle
            </LuxuryButton>
          )}
        </GlassCard>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <GlassCard className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-white">Binary Business</h3>
            <Network size={18} className="text-[var(--gold)]" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[["Left", data.left_business, "#2f6fd6"], ["Right", data.right_business, "#c8455f"], ["Matched", data.matched_business, "#C99A12"]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl glass-2 p-4">
                <p className="text-xs text-[var(--text-dim)]">{l} Business</p>
                <p className="mt-1 text-xl font-semibold" style={{ color: c }}>${money(v)}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={legData}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d9b36c" stopOpacity={0.6} /><stop offset="100%" stopColor="#d9b36c" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="name" stroke="#97a0b5" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0b0e17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
                <Area type="monotone" dataKey="v" stroke="#d9b36c" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-white">Target Progress</h3>
            <Trophy size={18} className="text-[var(--gold)]" />
          </div>
          {data.next_target ? (
            <>
              <p className="text-sm text-[var(--text-dim)]">Next target</p>
              <p className="gold-text font-display text-2xl font-semibold">${money(data.next_target.target, 0)}</p>
              <div className="mt-4">
                <ProgressBar value={data.matched_business} max={data.next_target.target} />
                <div className="mt-2 flex justify-between text-xs text-[var(--text-dim)]">
                  <span>${money(data.matched_business)} matched</span>
                  <span>${money(data.remaining_matched)} to go</span>
                </div>
              </div>
              <div className="mt-4 rounded-xl glass-2 p-3 text-sm">
                <p className="text-[var(--text-dim)]">Instant reward at next target</p>
                <p className="gold-text text-lg font-semibold">${money(data.next_target.instant, 0)}</p>
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--text-dim)]">All targets achieved. Outstanding!</p>
          )}
        </GlassCard>
      </div>

      {/* Global Pool */}
      <GlobalPools />

      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">Recent Transactions</h3>
        {data.recent?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[var(--text-dim)]"><tr className="border-b border-white/10">
                <th className="py-3">Type</th><th className="py-3">Description</th><th className="py-3">Status</th><th className="py-3 text-right">Amount</th></tr></thead>
              <tbody>
                {data.recent.map((t, i) => (
                  <tr key={i} className="row-hover border-b border-white/5">
                    <td className="py-3 capitalize text-white">{t.type?.replace(/_/g, " ")}</td>
                    <td className="py-3 text-[var(--text-dim)]">{t.description}</td>
                    <td className="py-3"><StatusBadge status={t.status} /></td>
                    <td className="py-3 text-right font-medium gold-text">+${money(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-[var(--text-dim)]">No transactions yet.</p>}
      </GlassCard>
    </div>

    {roiPopup && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        data-testid="roi-boost-popup-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="roi-boost-popup-title"
      >
        <div
          className="relative w-full max-w-md overflow-hidden rounded-3xl border border-[var(--gold)]/60 bg-[#07140d] p-7 text-center shadow-[0_25px_80px_rgba(0,0,0,0.65)]"
          data-testid="roi-boost-popup"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(217,179,108,0.18),transparent_55%)]" />
          <button
            type="button"
            onClick={() => setRoiPopup(null)}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white"
            data-testid="roi-boost-popup-close"
          >
            ×
          </button>
          <div className="relative">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[var(--gold)]/50 bg-[var(--gold)]/10 text-3xl">
              🎉
            </div>
            <h2 id="roi-boost-popup-title" className="mt-4 font-display text-2xl font-extrabold text-[var(--gold)]">
              Congratulations!
            </h2>
            <p className="mt-3 text-sm leading-6 text-white/80">
              {roiPopup.body}
            </p>
            <button
              type="button"
              onClick={() => setRoiPopup(null)}
              className="mt-6 inline-flex min-w-32 items-center justify-center rounded-xl bg-[var(--gold)] px-6 py-2.5 text-sm font-extrabold text-[#07140d] transition hover:brightness-110"
              data-testid="roi-boost-popup-ok"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function TradingTeaser({ status }) {
  const [rows, setRows] = React.useState([]);
  const [wallet, setWallet] = React.useState(null);
  const navigate = useNavigate();
  React.useEffect(() => {
    let m = true;
    const load = () => {
      api.get("/trading/market").then((r) => m && setRows((r.data.items || []).filter((i) => i.chart).slice(0, 6))).catch(() => {});
      api.get("/trading/wallet").then((r) => m && setWallet(r.data)).catch(() => {});
    };
    load();
    const id = setInterval(load, 8000);
    return () => { m = false; clearInterval(id); };
  }, []);
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 neon-pulse sm:p-6" data-testid="dashboard-trading-teaser"
      style={{ background: "linear-gradient(150deg,#06120c,#08221a 60%,#06120c)", border: "1px solid rgba(16,185,129,.4)" }}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">
            <CandlestickChart size={16} /> ALEXA Trading Terminal
            <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] text-emerald-300">LIVE MARKETS</span>
          </p>
          <h3 className="mt-1 font-display text-2xl font-extrabold text-white">Self-Trade & ALEXA Bot</h3>
          <p className="mt-0.5 text-sm text-emerald-200/70">Trade live-priced markets with your separate Trading Wallet.</p>
        </div>
        <button onClick={() => navigate("/app/trading")} data-testid="open-terminal-btn"
          className="flex items-center gap-2 rounded-2xl bg-emerald-500 px-6 py-3 text-sm font-extrabold text-black shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400">
          <ArrowUpRight size={18} /> Open Terminal
        </button>
      </div>
      {wallet && (
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[["Trading Wallet", wallet.balance], ["Available", wallet.available], ["In Trade", wallet.locked], ["Trading Profit", wallet.realized_pnl]].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-black/30 px-3 py-2" style={{ border: "1px solid rgba(16,185,129,.18)" }}>
              <p className="text-[10px] uppercase text-emerald-200/50">{l}</p>
              <p className={`font-mono text-sm font-bold ${v >= 0 ? "text-white" : "text-rose-400"}`}>${money(v)}</p>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {rows.length === 0 && <p className="col-span-2 text-xs text-emerald-200/50 sm:col-span-6">Loading live markets…</p>}
        {rows.map((mkt) => (
          <button key={mkt.symbol} onClick={() => navigate("/app/trading")} className="rounded-xl bg-black/30 p-3 text-left transition hover:ring-1 hover:ring-emerald-500/50" style={{ border: "1px solid rgba(255,255,255,.06)" }}>
            <p className="text-[11px] font-bold text-white">{mkt.symbol}</p>
            <p className="font-mono text-sm text-white">{mkt.price != null ? Number(mkt.price).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}</p>
            {mkt.change != null && <p className={`text-[10px] ${mkt.change >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{mkt.change >= 0 ? "+" : ""}{mkt.change}%</p>}
          </button>
        ))}
      </div>
    </div>
  );
}
